import { createWithEqualityFn } from 'zustand/traditional';
import { persist } from 'zustand/middleware';
import type { User } from '../types';
import { useDeviceInterfaceHistoryStore } from '@features/devices/store/deviceInterfaceHistoryStore';
import { appQueryClient } from '@shared/lib/appQueryClient';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  setUser: (user: User | null, accessToken?: string | null, refreshToken?: string | null) => void;
  logout: () => void;
}

const lsGet = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};

const lsSet = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* noop */ }
};

const lsRemove = (key: string): void => {
  try { localStorage.removeItem(key); } catch { /* noop */ }
};

const sameCapabilities = (left: string[] | undefined, right: string[] | undefined): boolean =>
  JSON.stringify([...(left ?? [])].sort()) === JSON.stringify([...(right ?? [])].sort());

const sameOperatorScope = (left: User | null, right: User | null): boolean =>
  left?.id === right?.id && left?.is_active === right?.is_active &&
  sameCapabilities(left?.roles, right?.roles) &&
  sameCapabilities(left?.permissions, right?.permissions);

export const useAuthStore = createWithEqualityFn<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: lsGet('access_token') || lsGet('token'),
      refreshToken: lsGet('refresh_token'),
      isAuthenticated: !!(lsGet('access_token') || lsGet('token')),

      setUser: (user, accessToken = null, refreshToken = null) => {
        // A successful login starts a new authorization boundary even if the
        // same operator logs in again: server-side address scope may change
        // without appearing in User. A no-token profile refresh only resets
        // cache when identity/capabilities change. Ordinary refresh-token
        // rotation does not call setUser and does not flush the whole SPA.
        if (accessToken || !sameOperatorScope(get().user, user)) {
          appQueryClient.clear(); // also destroys/cancels in-flight old queries
        }
        const finalToken = accessToken || lsGet('access_token') || lsGet('token');
        if (accessToken) {
          lsSet('access_token', accessToken);
          lsSet('token', accessToken);
        }
        if (refreshToken) lsSet('refresh_token', refreshToken);
        set({
          user,
          accessToken: finalToken,
          refreshToken: refreshToken || lsGet('refresh_token'),
          isAuthenticated: !!user
        });
      },

      logout: () => {
        lsRemove('access_token');
        lsRemove('token');
        lsRemove('workernet_access_token');
        lsRemove('refresh_token');
        appQueryClient.clear(); // before subscribers can render with old data
        // ✅ Очистка истории и закрепленных портов при выходе
        useDeviceInterfaceHistoryStore.getState().clear();
        // Токены обнуляются и в состоянии: иначе persist-блоб переживает выход
        // и токен остаётся лежать на диске браузера до истечения exp.
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false });
      },
    }),
    {
      name: 'workernet-auth-store', // Ключ в localStorage
      // Токены НЕ персистятся вовсе: единственный носитель на диске — сырые
      // ключи localStorage, которые выход удаляет. Блоб хранит только профиль.
      partialize: (state) => ({ user: state.user }),
      // merge отбрасывает всё, кроме профиля, даже если на диске лежит блоб,
      // записанный прежней версией кода (там были accessToken/refreshToken).
      merge: (persisted, current) => ({
        ...current,
        user: (persisted as { user?: User | null } | undefined)?.user ?? null,
      }),
    }
  )
);

